import { AgentTemplateKind, type SystemToolName } from '@reach/shared-types/agents';

interface TemplateCopy {
  title: string;
  description: string;
  editorTitle: string;
  editorExplainer: string;
}

export const TEMPLATES_COPY = {
  heading: 'Templates',
  description: "Each template decides which tools every tenant's agents of that kind can use.",
  loading: 'Loading templates…',
  loadError: 'Your agent templates could not be loaded. Refresh the page to try again.',
  toolsLoadError: 'Your tools could not be loaded. Refresh the page to edit template tools.',
  notProvisioned:
    'Agent templates are not set up for your account yet. Contact Reach support to finish setup.',
  saveFailed: 'That change could not be saved.',
  syncQueueFailed:
    'Saved, but your AI phone agents could not be updated yet. Contact Reach support if this keeps happening.',
  saved: 'Saved',
  voiceSavedDetail: 'AI phone agents update in the background.',
} as const;

export const TEMPLATE_COPY = {
  [AgentTemplateKind.SMS]: {
    title: 'SMS agents',
    description: "Every tenant's SMS agent can use these tools.",
    editorTitle: 'Tools for SMS agents',
    editorExplainer:
      "Checked tools are available to every tenant's SMS agent. Unchecking a tool removes it from all of them.",
  },
  [AgentTemplateKind.VOICE]: {
    title: 'AI phone agents',
    description: "Every tenant's AI phone agent can use these tools.",
    editorTitle: 'Tools for AI phone agents',
    editorExplainer:
      "Checked tools are available to every tenant's AI phone agent. Unchecking a tool removes it from all of them. Live phone agents update within a few minutes.",
  },
} as const satisfies Record<AgentTemplateKind, TemplateCopy>;

export const CARD_COPY = {
  manageTools: 'Manage tools',
  noTools: 'No tools yet.',
  unavailableTool: 'This tool is paused or no longer exists, so agents cannot use it.',
} as const;

export const EDITOR_COPY = {
  close: 'Close',
  builtInLegend: 'Built-in tools',
  requiredBadge: 'Required',
  requiredHint: 'Always on. Agents cannot work without it.',
  yourToolsLegend: 'Your tools',
  noToolsBeforeLink: 'You have no active tools yet. Create one on the',
  noToolsLinkLabel: 'Tools',
  noToolsAfterLink: 'tab.',
  unavailableHeading: 'Unavailable tools',
  unavailableHint:
    'These tools are paused or no longer exist, so agents cannot use them. Remove them to keep this list tidy.',
  remove: 'Remove',
  unknownTool: 'Unknown tool',
} as const;

const SYSTEM_TOOL_LABELS = {
  queue_sms: 'Send text messages',
} as const satisfies Partial<Record<SystemToolName, string>>;

const SYSTEM_TOOL_LABEL_BY_NAME: Partial<Record<SystemToolName, string>> = SYSTEM_TOOL_LABELS;

export const systemToolLabel = (systemToolName: SystemToolName): string =>
  SYSTEM_TOOL_LABEL_BY_NAME[systemToolName] ?? systemToolName;
